/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo, useState } from "react";
import { Box, Chip, Divider, MenuList, Popover, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { isLightColor } from "../../../utils/color_helpers";

const DEFAULT_CARD_HEIGHT = '23px'
const CHIP_TEXT_COLOR = 'rgba(0, 0, 0, 0.87)'
const DARK_CHIP_TEXT_COLOR = 'white'

const getDefaultCardTextColor = (color) => (
  isLightColor(color?.toLowerCase()) ? CHIP_TEXT_COLOR : DARK_CHIP_TEXT_COLOR
)

const getAssignedDiscountCardId = (card) => card?.card_id ?? card?.id

const getAssignedDiscountCardOptionKey = (card) => getAssignedDiscountCardId(card)

export const DefaultCardSelector = ({
  membershipId,
  card,
  assignedCards = [],
  canUpdate,
  isLoading,
  onUpdate,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const hasCard = Boolean(card?.id)
  const hasAssignedCards = assignedCards.length > 0
  const isClickable = hasAssignedCards && canUpdate && typeof onUpdate === 'function' && !isLoading
  const backgroundColor = hasCard ? card?.color : 'white'
  const uniqueAssignedCards = useMemo(() => {
    const seenCardIds = new Set()

    return assignedCards.filter((assignedCard) => {
      const assignedCardId = getAssignedDiscountCardId(assignedCard)
      if (!assignedCardId || seenCardIds.has(assignedCardId)) return false

      seenCardIds.add(assignedCardId)
      return true
    })
  }, [assignedCards])

  const currentDefaultCardOptionKey = useMemo(() => {
    if (!card?.id) return null

    const selectedIndex = uniqueAssignedCards.findIndex(
      (assignedCard) => getAssignedDiscountCardId(assignedCard) === card.id
    )

    if (selectedIndex === -1) return null

    return getAssignedDiscountCardOptionKey(uniqueAssignedCards[selectedIndex])
  }, [card?.id, uniqueAssignedCards])

  const handleOpen = (event) => {
    if (!isClickable) return

    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (isLoading) return

    setAnchorEl(null)
  }

  const handleUpdate = async (discountCard) => {
    const didUpdate = await onUpdate?.(discountCard)

    if (didUpdate !== false) {
      setAnchorEl(null)
    }
  }

  return (
    <>
      <Box
        data-testid={`default-card-trigger-${membershipId}`}
        onClick={handleOpen}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: isClickable ? 'pointer' : 'default',
        }}
      >
        <Chip
          label={card?.name || 'No cards'}
          size="small"
          clickable={false}
          sx={{
            fontWeight: 550,
            height: DEFAULT_CARD_HEIGHT,
            backgroundColor,
            color: hasCard ? getDefaultCardTextColor(backgroundColor) : CHIP_TEXT_COLOR,
            border: hasCard ? 'none' : '2px solid lightgray',
            '&:hover': {
              backgroundColor,
            },
          }}
        />

        {isClickable && (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '18px',
              color: '#707070',
            }}
          />
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: '220px',
            borderRadius: '10px',
            mt: '5px',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
            Select default card
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
            Choose one of this member&apos;s assigned discount cards
          </Typography>
        </Box>

        <Divider />

        <MenuList dense disablePadding>
          {uniqueAssignedCards.map((assignedCard) => {
            const optionKey = getAssignedDiscountCardOptionKey(assignedCard)
            const isCurrentCard = currentDefaultCardOptionKey === optionKey

            return (
              <StyledMenuItem
                key={optionKey}
                data-testid={`default-card-option-${membershipId}-${optionKey}`}
                disabled={isCurrentCard || isLoading}
                onClick={() => handleUpdate(assignedCard)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minWidth: '220px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Chip
                    label={assignedCard?.name}
                    size="small"
                    sx={{
                      fontWeight: 550,
                      height: DEFAULT_CARD_HEIGHT,
                      backgroundColor: assignedCard?.color,
                      color: getDefaultCardTextColor(assignedCard?.color),
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </Box>

                {isCurrentCard && <CheckIcon sx={{ color: '#2b4fb9', fontSize: '18px' }} />}
              </StyledMenuItem>
            )
          })}
        </MenuList>
      </Popover>
    </>
  )
}
